import { useMutation, useQuery } from "@apollo/client";
import { createAjv } from "@jsonforms/core";
import _ from "lodash";
import { Button, LoadingSpinner } from "pepsico-ds";
import PropTypes from "prop-types";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { GlobalConfigContext } from "../../../context/GlobalConfigContext";
import updateProgramConfiguration from "../../../graphql/mutations/updateProgramConfiguration";
import { loadUiComponent } from "../../../graphql/queries/loadUiComponent";
import { FeatureToggle, useFeatureToggle } from "../../../utils/featureToggle";
import { normalizeUserLoginLimits } from "../../../utils/userLoginLimits";
import ToastNotification from "../../common/toastNotification/ToastNotification";
import JsonFormsWrapper from "../../jsonForms/jsonFormsWrapper/JsonFormsWrapper";
import { PointsConversionPreviewComponent } from "../../jsonForms/pointsConversionPreview/PointsConversionPreview";
import { PointsLifecycleSettingsView } from "../../jsonForms/pointsLifecycleSettings/PointsLifecycleSettings";
import { normalizeGlobalPointsConfiguration } from "../../jsonForms/pointsLifecycleSettings/pointsLifecycleSettingsUtils";
import { SocialMediaSelectorComponent } from "../../jsonForms/socialMediaSelector/SocialMediaSelector";
import ConfigurationModals from "../PartnerGenericConfigurationPages/ConfigurationModals";
import SocialMediaConfigurationView from "../SocialMediaLinks/SocialMediaConfigurationView";
import { WidgetDetailContent } from "../WidgetPages/WidgetDetailPage";
import { isWidgetConfigurationValid } from "../WidgetPages/widgetConfigurationUtils";

/**
 * Converts a property key to a human-readable label by splitting on
 * underscores, hyphens, and camelCase boundaries.
 */
function propertyKeyToLabel(key) {
  return key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

/**
 * Extracts a dot-notation path from a JSON Schema scope string.
 * e.g. "#/properties/foo" → "foo", "#/properties/a/properties/b" → "a.b"
 */
function scopeToPath(scope) {
  if (!scope) return null;
  const cleaned = scope.replace(/^#\//, "").replace(/\/$/, "");
  return cleaned
    .split("/")
    .filter((p) => p !== "properties")
    .join(".");
}

/**
 * Resolves a JSON Schema property definition at a given dot-notation path.
 */
function getSchemaPropertyAtPath(path, schema) {
  const parts = path.split(".");
  let current = schema;
  for (const part of parts) {
    if (current?.properties?.[part]) {
      current = current.properties[part];
    } else {
      return null;
    }
  }
  return current;
}

/**
 * Generates DrawerFieldRenderer-compatible field definitions from
 * a JSON Forms uiSchema and jsonSchema. Section headers come from
 * top-level Label elements; field labels come from Control labels
 * or are derived from the property key.
 */
function generateFieldsFromSchemas(uiSchemaNode, jsonSchemaNode) {
  const fields = [];
  let currentSection = null;
  const summaryCustomComponents = new Set(["pointsConversionPreview"]);

  function getFieldType(schemaProp) {
    if (!schemaProp) return "text";
    if (schemaProp.type === "boolean") return "toggle";
    if (schemaProp.type === "integer" || schemaProp.type === "number")
      return "number";
    return "text";
  }

  function traverse(element) {
    if (!element) return;

    // Section header: Label without component property
    if (element.type === "Label" && element.text && !element.component) {
      currentSection = element.text;
      return;
    }

    // Control → field
    if (element.type === "Control" && element.scope) {
      const path = scopeToPath(element.scope);
      if (path && path !== "labelHelper") {
        const schemaProp = getSchemaPropertyAtPath(path, jsonSchemaNode);

        if (summaryCustomComponents.has(element.component)) {
          fields.push({
            path,
            label: element.label
              ? element.label.replace(/\*$/, "").trim()
              : schemaProp?.title || propertyKeyToLabel(path.split(".").pop()),
            type: "custom",
            component: element.component,
            section: currentSection || undefined,
          });
          return;
        }

        // Custom components on complex types: include with component info
        if (
          (schemaProp?.type === "object" || schemaProp?.type === "array") &&
          element.component
        ) {
          fields.push({
            path,
            label: element.label
              ? element.label.replace(/\*$/, "").trim()
              : propertyKeyToLabel(path.split(".").pop()),
            type: "custom",
            component: element.component,
            section: currentSection || undefined,
          });
          return;
        }

        // Skip complex types without custom components
        if (schemaProp?.type === "object" || schemaProp?.type === "array") return;

        const label = element.label
          ? element.label.replace(/\*$/, "").trim()
          : schemaProp?.title || propertyKeyToLabel(path.split(".").pop());

        const field = {
          path,
          label,
          type: getFieldType(schemaProp),
          section: currentSection || undefined,
        };

        if (element.options?.labelMapping) {
          field.valueMappings = element.options.labelMapping;
        }

        fields.push(field);
      }
      return;
    }

    // Recurse into layouts
    if (Array.isArray(element.elements)) {
      element.elements.forEach((child) => traverse(child));
    }
  }

  traverse(uiSchemaNode);
  return fields;
}

/**
 * Deep value accessor for nested objects using dot-notation paths.
 */
function getDeepValue(obj, path) {
  if (!obj || !path) return undefined;
  return path.split(".").reduce((acc, part) => acc?.[part], obj);
}

/**
 * Custom component map used by JsonFormsViewFields to render
 * complex controls (e.g. socialMediaSelector) in readonly view mode.
 */
const customComponentMap = {
  socialMediaSelector: ({ field, data }) => (
    <SocialMediaSelectorComponent
      key={field.path}
      data={data || {}}
      path={field.path}
      handleChange={() => {}}
      label={field.label}
      config={{ readonly: true }}
    />
  ),
  pointsLifecycleSettings: ({ field, data }) => (
    <PointsLifecycleSettingsView key={field.path} data={data || {}} />
  ),
  pointsConversionPreview: ({ field, data }) => (
    <PointsConversionPreviewComponent key={field.path} data={data} />
  ),
};

const WHATSAPP_CONTACT_LINK_FIELD = "whatsapp_contact_link";
const WHATSAPP_CONTACT_LINK_SCOPE = `#/properties/${WHATSAPP_CONTACT_LINK_FIELD}`;

function getProgramConfigurationData(programConfigurations, key) {
  const configData = _.find(programConfigurations, { key });
  return configData?.value?.data
    ? JSON.parse(JSON.stringify(configData.value.data))
    : {};
}

function mergeDataForSharedConfig(configKey, currentData, dataToSave) {
  if (configKey !== "global_points_configuration") {
    return dataToSave;
  }

  return normalizeGlobalPointsConfiguration({
    ...currentData,
    ...dataToSave,
  });
}

function hasUiControl(uiSchemaNode, scope) {
  if (!uiSchemaNode) return false;
  if (uiSchemaNode.type === "Control" && uiSchemaNode.scope === scope) {
    return true;
  }
  return Array.isArray(uiSchemaNode.elements)
    ? uiSchemaNode.elements.some((element) => hasUiControl(element, scope))
    : false;
}

function removeUiControl(uiSchemaNode, scope) {
  if (!uiSchemaNode) return uiSchemaNode;
  if (uiSchemaNode.type === "Control" && uiSchemaNode.scope === scope) {
    return null;
  }

  const clonedNode = { ...uiSchemaNode };
  if (Array.isArray(uiSchemaNode.elements)) {
    clonedNode.elements = uiSchemaNode.elements
      .map((element) => removeUiControl(element, scope))
      .filter(Boolean);
  }

  return clonedNode;
}

function appendWhatsappContactLinkSchema(jsonSchemaNode, uiSchemaNode) {
  const jsonSchemaWithWhatsapp = {
    ...jsonSchemaNode,
    properties: {
      ...(jsonSchemaNode?.properties || {}),
      [WHATSAPP_CONTACT_LINK_FIELD]: {
        ...(jsonSchemaNode?.properties?.[WHATSAPP_CONTACT_LINK_FIELD] || {}),
        title: "WhatsApp Contact Link",
        type: "string",
      },
    },
  };

  if (hasUiControl(uiSchemaNode, WHATSAPP_CONTACT_LINK_SCOPE)) {
    return {
      jsonSchema: jsonSchemaWithWhatsapp,
      uiSchema: uiSchemaNode,
    };
  }

  return {
    jsonSchema: jsonSchemaWithWhatsapp,
    uiSchema: {
      ...uiSchemaNode,
      elements: [
        ...(uiSchemaNode?.elements || []),
        {
          type: "Label",
          text: "Contact Settings",
        },
        {
          type: "VerticalLayout",
          elements: [
            {
              type: "HorizontalLayout",
              elements: [
                {
                  type: "Control",
                  scope: WHATSAPP_CONTACT_LINK_SCOPE,
                  component: "textField",
                  label: "WhatsApp Contact Link",
                  placeholder: "Enter WhatsApp contact link",
                },
              ],
            },
          ],
        },
      ],
    },
  };
}

function resolveConfigSchemas(configKey, jsonSchemaNode, uiSchemaNode) {
  if (configKey === "app_configurations") {
    return appendWhatsappContactLinkSchema(jsonSchemaNode, uiSchemaNode);
  }

  if (configKey === "generic_configuration") {
    return {
      jsonSchema: jsonSchemaNode,
      uiSchema: removeUiControl(uiSchemaNode, WHATSAPP_CONTACT_LINK_SCOPE),
    };
  }

  return {
    jsonSchema: jsonSchemaNode,
    uiSchema: uiSchemaNode,
  };
}

/**
 * Renders view-mode fields generated from uiSchema / jsonSchema.
 * Handles both simple label-value fields and custom components
 * (e.g. socialMediaSelector) grouped by section.
 */
const JsonFormsViewFields = ({ fields, formData }) => {
  const groupedFields = fields.reduce((groups, field) => {
    const section = field.section || "__default__";
    if (!groups[section]) groups[section] = [];
    groups[section].push(field);
    return groups;
  }, {});

  return (
    <div className="config-drawer-fields">
      {Object.entries(groupedFields).map(([section, sectionFields]) => (
        <div key={section} className="config-drawer-section">
          {section !== "__default__" && (
            <p className="config-drawer-section-title">{section}</p>
          )}
          {sectionFields.map((field) => {
            if (field.type === "custom" && customComponentMap[field.component]) {
              const CustomComp = customComponentMap[field.component];
              return (
                <CustomComp
                  key={field.path}
                  field={field}
                  data={getDeepValue(formData, field.path)}
                />
              );
            }

            const value = getDeepValue(formData, field.path);
            const displayValue =
              field.valueMappings?.[value] ??
              (value !== undefined && value !== null ? value.toString() : "-");

            if (field.type === "toggle") {
              return (
                <div key={field.path} className="config-drawer-field view">
                  <span className="config-drawer-field-label">{field.label}</span>
                  <span className="config-drawer-field-value">
                    {value ? "Yes" : "No"}
                  </span>
                </div>
              );
            }

            return (
              <div key={field.path} className="config-drawer-field view">
                <label className="config-drawer-field-label">{field.label}:</label>
                <span className="config-drawer-field-value">{displayValue}</span>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

/**
 * Renders JsonForms-based configuration content inside the drawer.
 * Supports user_login_limits, app_configurations, social_media_links, widgets_configuration.
 */
const JsonFormsDrawerContent = ({
  configKey,
  mode,
  isOpen,
  onClose,
  onEdit,
  canEdit,
  schemaSlug,
  preloadedSchemas,
}) => {
  const {
    programConfigurations,
    updateProgramConfigurations,
    globalState: { programConfig },
  } = useContext(GlobalConfigContext);

  const [updateProgramConfigurationMutation, { loading: mutationLoading }] =
    useMutation(updateProgramConfiguration);

  const [formData, setFormData] = useState({});
  const [initialFormData, setInitialFormData] = useState({});
  const [uiSchema, setUiSchema] = useState(null);
  const [jsonSchema, setJsonSchema] = useState(null);
  const [schemaError, setSchemaError] = useState(false);
  const [formErrors, setFormErrors] = useState([]);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [discardModalOpen, setDiscardModalOpen] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    type: "",
    title: "",
    description: "",
  });

  const syncFlag = useRef(false);
  const isEdit = mode === "edit";
  const isNewConfigEnabled = useFeatureToggle(
    FeatureToggle.enableNewConfigLimitCodeScan
  );

  // Fetch schemas from API if schemaSlug is provided
  const { refetch: fetchSchemas } = useQuery(loadUiComponent, {
    variables: { slug: schemaSlug || configKey },
    skip: true,
    errorPolicy: "all",
  });

  // Load schemas - from preloaded props, API, or inline
  useEffect(() => {
    if (!isOpen) {
      syncFlag.current = false;
      setFormErrors([]);
      return;
    }

    if (preloadedSchemas?.jsonSchema && preloadedSchemas?.uiSchema) {
      const resolvedSchemas = resolveConfigSchemas(
        configKey,
        preloadedSchemas.jsonSchema,
        preloadedSchemas.uiSchema
      );
      setJsonSchema(resolvedSchemas.jsonSchema);
      setUiSchema(resolvedSchemas.uiSchema);
    } else if (schemaSlug) {
      loadSchemasFromApi();
    } else {
      loadInlineSchemas();
    }
  }, [isOpen, configKey, preloadedSchemas]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSchemasFromApi = async () => {
    try {
      setSchemaError(false);
      const res = await fetchSchemas();
      if (res?.data?.loadUiComponent) {
        const resolvedSchemas = resolveConfigSchemas(
          configKey,
          res.data.loadUiComponent.inputSchema,
          res.data.loadUiComponent.uiSchema
        );
        setUiSchema(resolvedSchemas.uiSchema);
        setJsonSchema(resolvedSchemas.jsonSchema);
      } else {
        setSchemaError(true);
      }
    } catch (error) {
      console.error(`Failed to load ${configKey} schema:`, error);
      setSchemaError(true);
    }
  };

  const loadInlineSchemas = () => {
    if (configKey === "user_login_limits") {
      setJsonSchema(getUserLoginLimitsJsonSchema(isNewConfigEnabled));
      setUiSchema(getUserLoginLimitsUiSchema(isNewConfigEnabled));
    } else if (configKey === "social_media_links") {
      setJsonSchema(getSocialMediaLinksJsonSchema());
      setUiSchema(getSocialMediaLinksUiSchema());
    }
  };

  // Load configuration data
  useEffect(() => {
    if (!isOpen) return;

    const storedData = getProgramConfigurationData(programConfigurations, configKey);
    if (Object.keys(storedData).length > 0) {
      const data =
        configKey === "user_login_limits"
          ? normalizeUserLoginLimits(storedData)
          : storedData;
      const dataWithLinkedFields =
        configKey === "app_configurations"
          ? {
              ...data,
              [WHATSAPP_CONTACT_LINK_FIELD]:
                getProgramConfigurationData(
                  programConfigurations,
                  "generic_configuration"
                )?.[WHATSAPP_CONTACT_LINK_FIELD] || "",
            }
          : data;
      if (!syncFlag.current) {
        setInitialFormData(dataWithLinkedFields);
        setFormData(dataWithLinkedFields);
        syncFlag.current = true;
      }
    }
  }, [programConfigurations, configKey, isOpen]);

  const isUpdated = useMemo(() => {
    return JSON.stringify(initialFormData) !== JSON.stringify(formData);
  }, [initialFormData, formData]);

  const hasErrors = useMemo(() => {
    const hasJsonFormsErrors = Array.isArray(formErrors) && formErrors.length > 0;
    if (hasJsonFormsErrors) return true;

    if (configKey === "widgets_configuration") {
      return !isWidgetConfigurationValid(formData);
    }

    if (configKey !== "social_media_links" || !jsonSchema) return false;

    return !createAjv().validate(jsonSchema, formData);
  }, [configKey, formData, formErrors, jsonSchema]);

  const viewFields = useMemo(() => {
    if (!isEdit && jsonSchema && uiSchema) {
      return generateFieldsFromSchemas(uiSchema, jsonSchema);
    }
    return [];
  }, [isEdit, jsonSchema, uiSchema]);

  const handleSaveConfirmation = async () => {
    const dataToSave =
      configKey === "user_login_limits"
        ? normalizeUserLoginLimits(formData)
        : formData;
    const currentConfigData = getProgramConfigurationData(
      programConfigurations,
      configKey
    );
    const mergedDataToSave = mergeDataForSharedConfig(
      configKey,
      currentConfigData,
      dataToSave
    );
    const appConfigurationsData =
      configKey === "app_configurations"
        ? Object.fromEntries(
            Object.entries(mergedDataToSave).filter(
              ([key]) => key !== WHATSAPP_CONTACT_LINK_FIELD
            )
          )
        : mergedDataToSave;
    const params = {
      variables: {
        input: {
          slug: configKey,
          data: JSON.stringify(appConfigurationsData),
        },
        id: programConfig?.id,
      },
    };

    try {
      const res = await updateProgramConfigurationMutation(params);
      let updateResult = res?.data?.updateProgramConfiguration;

      if (configKey === "app_configurations") {
        const genericConfigurationData = getProgramConfigurationData(
          programConfigurations,
          "generic_configuration"
        );
        const genericRes = await updateProgramConfigurationMutation({
          variables: {
            input: {
              slug: "generic_configuration",
              data: JSON.stringify({
                ...genericConfigurationData,
                [WHATSAPP_CONTACT_LINK_FIELD]:
                  dataToSave?.[WHATSAPP_CONTACT_LINK_FIELD] || "",
              }),
            },
            id: programConfig?.id,
          },
        });
        updateResult = genericRes?.data?.updateProgramConfiguration || updateResult;
      }

      if (updateResult) {
        updateProgramConfigurations(updateResult?.configurations);
        setInitialFormData(JSON.parse(JSON.stringify(mergedDataToSave)));
        setFormData(mergedDataToSave);
        setToast({
          open: true,
          type: "success",
          title: "Settings updated successfully!",
          description: `You have just saved the ${configKey.replace(/_/g, " ")}`,
        });
      }
    } catch {
      setToast({
        open: true,
        type: "error",
        title: "Something went wrong!",
        description: "Please try again later.",
      });
    }
    setConfirmationModalOpen(false);
  };

  const handleClose = () => {
    if (isEdit && isUpdated) {
      setDiscardModalOpen(true);
    } else {
      onClose();
    }
  };

  const handleDiscard = () => {
    setFormData(JSON.parse(JSON.stringify(initialFormData)));
    setDiscardModalOpen(false);
    onClose();
  };

  const schemasLoaded = uiSchema && jsonSchema;
  const isLoading = !schemasLoaded && !schemaError;

  if (isLoading) {
    return (
      <>
        <div className="config-drawer-loading">
          <LoadingSpinner size={{ height: "2rem", width: "2rem" }} />
        </div>
        <div className="config-drawer-actions">
          <Button size="medium" variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </>
    );
  }

  if (schemaError) {
    return (
      <>
        <div className="config-drawer-error">
          <p>Failed to load configuration settings. Please try again later.</p>
        </div>
        <div className="config-drawer-actions">
          <Button size="medium" variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      {configKey === "generic_configuration" && (
        <div className="config-drawer-program-id">
          <span className="config-drawer-program-id-label">
            Program Identification
          </span>
          <span className="config-drawer-program-id-value">
            {localStorage.getItem("programId") || "-"}
          </span>
        </div>
      )}

      <div className="config-drawer-jsonforms-content">
        {configKey === "social_media_links" ? (
          <SocialMediaConfigurationView
            data={formData}
            isEdit={isEdit}
            onChange={setFormData}
          />
        ) : configKey === "widgets_configuration" ? (
          <WidgetDetailContent
            isEdit={isEdit}
            onChange={setFormData}
            variant="drawer"
            widgetData={formData}
          />
        ) : isEdit ? (
          <JsonFormsWrapper
            data={formData}
            onChange={(data, errors) => {
              setFormData(data);
              setFormErrors(errors);
            }}
            uischema={uiSchema}
            jsonschema={jsonSchema}
          />
        ) : (
          <JsonFormsViewFields fields={viewFields} formData={formData} />
        )}
      </div>

      <div className="config-drawer-actions">
        {isEdit ? (
          <>
            <Button size="medium" variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              size="medium"
              variant="primary"
              disabled={!isUpdated || mutationLoading || hasErrors}
              onClick={() => setConfirmationModalOpen(true)}
            >
              Save
            </Button>
          </>
        ) : canEdit && onEdit ? (
          <>
            <Button size="medium" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button size="medium" variant="primary" onClick={onEdit}>
              Edit
            </Button>
          </>
        ) : (
          <Button size="medium" variant="secondary" onClick={onClose}>
            Close
          </Button>
        )}
      </div>

      <ConfigurationModals
        confirmationModalOpen={confirmationModalOpen}
        discardModalOpen={discardModalOpen}
        setConfirmationModalOpen={setConfirmationModalOpen}
        setDiscardModalOpen={() => setDiscardModalOpen(false)}
        isLoading={mutationLoading}
        handleSaveConfirmation={handleSaveConfirmation}
        navigateToSystemSettings={handleDiscard}
        configType={configKey.replace(/_/g, " ")}
      />

      <ToastNotification
        {...toast}
        handleClose={() => setToast({ ...toast, open: false })}
      />
    </>
  );
};

JsonFormsDrawerContent.propTypes = {
  configKey: PropTypes.string.isRequired,
  mode: PropTypes.oneOf(["edit", "view"]).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onEdit: PropTypes.func,
  canEdit: PropTypes.bool,
  schemaSlug: PropTypes.string,
  preloadedSchemas: PropTypes.shape({
    jsonSchema: PropTypes.object,
    uiSchema: PropTypes.object,
  }),
};

export default JsonFormsDrawerContent;

// ─── Inline Schema Definitions ──────────────────────────────────────────────

function getUserLoginLimitsJsonSchema(isNewConfigEnabled) {
  return {
    title: "User Login Limit Settings",
    type: "object",
    required: [
      "maxRegisteredDevices",
      "maxRegisteredDevicesTimeWindow",
      "maxLoginsPerDevice",
      "maxLoginsPerDeviceTimeWindow",
      ...(isNewConfigEnabled
        ? [
            "invalidCodeMaxAttempts",
            "invalidCodeTimeframeMinutes",
            "invalidCodeLockoutDurationMinutes",
          ]
        : ["maxScanCount", "maxScanCountTimeWindow"]),
    ],
    properties: {
      labelHelper: { type: "string" },
      maxRegisteredDevices: { type: "integer" },
      maxRegisteredDevicesTimeWindow: { type: "integer" },
      maxLoginsPerDevice: { type: "integer" },
      maxLoginsPerDeviceTimeWindow: { type: "integer" },
      maxScanCount: { type: "integer" },
      maxScanCountTimeWindow: { type: "integer" },
      invalidCodeMaxAttempts: { type: "integer" },
      invalidCodeTimeframeMinutes: { type: "integer" },
      invalidCodeLockoutDurationMinutes: { type: "integer" },
    },
  };
}

function getUserLoginLimitsUiSchema(isNewConfigEnabled) {
  return {
    type: "VerticalLayout",
    elements: [
      {
        type: "Label",
        text: "User Device Limit Settings",
      },
      {
        type: "VerticalLayout",
        elements: [
          {
            type: "Label",
            text: "A user can login/register on maximum X devices in last Y days.",
            scope: "#/properties/labelHelper",
            component: "Label",
          },
          {
            type: "HorizontalLayout",
            elements: [
              {
                type: "Control",
                scope: "#/properties/maxRegisteredDevices",
                label: "Maximum number of devices registration*",
                tooltipText: "TODO",
                component: "numberInput",
                placeholder: "Type number",
              },
              {
                type: "Control",
                scope: "#/properties/maxRegisteredDevicesTimeWindow",
                label: "Number of days*",
                tooltipText: "TODO",
                component: "numberInput",
                placeholder: "Type number",
              },
            ],
          },
        ],
      },
      {
        type: "Label",
        text: "User Login Limit Settings",
      },
      {
        type: "VerticalLayout",
        elements: [
          {
            type: "Label",
            text: "On one device, a maximum of X logins/registrations can happen in the last Y days.",
            scope: "#/properties/labelHelper",
            component: "Label",
          },
          {
            type: "HorizontalLayout",
            elements: [
              {
                type: "Control",
                scope: "#/properties/maxLoginsPerDevice",
                label: "Maximum logins allowed per device*",
                tooltipText: "TODO",
                component: "numberInput",
                placeholder: "Type number",
              },
              {
                type: "Control",
                scope: "#/properties/maxLoginsPerDeviceTimeWindow",
                label: "Number of days*",
                tooltipText: "TODO",
                component: "numberInput",
                placeholder: "Type number",
              },
            ],
          },
        ],
      },
      {
        type: "Label",
        text: "User Successful Code Scan Limit Settings",
      },
      {
        type: "VerticalLayout",
        elements: [
          {
            type: "Label",
            text: isNewConfigEnabled
              ? "A user will be blocked for Z minutes after X invalid code attempts within Y minutes."
              : "Maximum number of X code scans are allowed per user account in Y minutes.",
            scope: "#/properties/labelHelper",
            component: "Label",
          },
          ...(isNewConfigEnabled
            ? [
                {
                  type: "HorizontalLayout",
                  elements: [
                    {
                      type: "Control",
                      scope: "#/properties/invalidCodeMaxAttempts",
                      label: "Maximum invalid code attempts before blocking*",
                      placeholder: "Type number",
                    },
                    {
                      type: "Control",
                      scope: "#/properties/invalidCodeTimeframeMinutes",
                      label: "Timeframe (Minutes)*",
                      placeholder: "Type number",
                    },
                    {
                      type: "Control",
                      scope: "#/properties/invalidCodeLockoutDurationMinutes",
                      label: "Lockout Duration (Minutes)*",
                      placeholder: "Type number",
                    },
                  ],
                },
              ]
            : [
                {
                  type: "HorizontalLayout",
                  elements: [
                    {
                      type: "Control",
                      scope: "#/properties/maxScanCount",
                      label: "Total Scan Count*",
                      placeholder: "Type number",
                    },
                    {
                      type: "Control",
                      scope: "#/properties/maxScanCountTimeWindow",
                      label: "Time Window (Minutes)*",
                      placeholder: "Type number",
                    },
                  ],
                },
              ]),
        ],
      },
    ],
  };
}

function getSocialMediaLinksJsonSchema() {
  const socialPlatformSchema = {
    type: "object",
    properties: {
      url: { type: "string", format: "uri" },
      position: { type: "number" },
      image: { type: "string", format: "uri" },
      title: { type: "string" },
      active: { type: "boolean" },
    },
  };

  return {
    title: "Social Media Links Configurations",
    type: "object",
    properties: {
      socialLinksColor: {
        type: "object",
        properties: {
          youtube: socialPlatformSchema,
          instagram: socialPlatformSchema,
          tiktok: socialPlatformSchema,
          facebook: socialPlatformSchema,
          x: socialPlatformSchema,
        },
      },
      socialLinksGrayscale: {
        type: "object",
        properties: {
          youtube: socialPlatformSchema,
          instagram: socialPlatformSchema,
          tiktok: socialPlatformSchema,
          facebook: socialPlatformSchema,
          x: socialPlatformSchema,
        },
      },
      homepageIcons: {
        type: "string",
        enum: ["COLOR", "GRAYSCALE"],
      },
      profileIcons: {
        type: "string",
        enum: ["COLOR", "GRAYSCALE"],
      },
      socialMediaText: {
        type: "string",
      },
    },
    required: [
      "socialLinksColor",
      "socialLinksGrayscale",
      "homepageIcons",
      "profileIcons",
      "socialMediaText",
    ],
  };
}

function getSocialMediaLinksUiSchema() {
  return {
    type: "VerticalLayout",
    elements: [
      {
        type: "VerticalLayout",
        elements: [
          {
            type: "HorizontalLayout",
            elements: [
              {
                component: "socialMediaSelector",
                label: "Icons in color",
                type: "Control",
                scope: "#/properties/socialLinksColor/",
              },
              {
                component: "socialMediaSelector",
                label: "Icons in grayscale",
                type: "Control",
                scope: "#/properties/socialLinksGrayscale/",
              },
            ],
          },
        ],
      },
      {
        type: "VerticalLayout",
        elements: [
          {
            type: "Label",
            text: "Icon color options for pages",
            component: "Label",
          },
          {
            type: "HorizontalLayout",
            elements: [
              {
                type: "HorizontalLayout",
                elements: [
                  {
                    options: {
                      labelMapping: { COLOR: "Color", GRAYSCALE: "Grayscale" },
                    },
                    component: "radioInput",
                    label: "Homepage icons",
                    type: "Control",
                    scope: "#/properties/homepageIcons",
                  },
                  {
                    options: {
                      labelMapping: { COLOR: "Color", GRAYSCALE: "Grayscale" },
                    },
                    component: "radioInput",
                    label: "Profile icons",
                    type: "Control",
                    scope: "#/properties/profileIcons",
                  },
                ],
              },
              { type: "VerticalLayout", elements: [] },
            ],
          },
        ],
      },
      {
        type: "VerticalLayout",
        elements: [
          {
            type: "Label",
            text: "Social media text configuration",
            component: "Label",
          },
          {
            type: "HorizontalLayout",
            elements: [
              {
                type: "Control",
                scope: "#/properties/socialMediaText",
                placeholder: "Type the social media text here...",
                size: "small",
                label: "Homepage social media text",
                info: "Character limit: 60",
                minLength: 3,
                maxLength: 60,
              },
              { type: "VerticalLayout", elements: [] },
            ],
          },
        ],
      },
    ],
  };
}
